import { useMemo, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Ship, TrendingUp, Package, DollarSign } from "lucide-react";
import { useEmbarques } from "@/hooks/useEmbarques";
import { useAllDocuments, useInventory } from "@/hooks/useDocuments";
import { useTRM, useTRMFecha } from "@/hooks/useTRM";
import { calcShipmentLandedCost } from "@/hooks/useShipmentLandedCost";
import { useComplianceLookup } from "@/hooks/useComplianceLookup";

const copFmt = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0, maximumFractionDigits: 0 });
const usdFmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ACTIVE_STATES = new Set(["En Origen", "En Tránsito", "En Puerto"]);

export function DashboardKPIs() {
  const { data: embarques = [] } = useEmbarques();
  const { data: allDocs = [] } = useAllDocuments();
  const { trmMap, trm } = useTRM();
  const trmFecha = useTRMFecha("USD");
  const { results: complianceResults, lookup: complianceLookup } = useComplianceLookup();

  // Active embarques (not Nacionalizado)
  const activeEmbarques = useMemo(
    () => embarques.filter((e) => ACTIVE_STATES.has(e.estado_logistico)),
    [embarques]
  );

  const activeEmbarqueIds = useMemo(
    () => new Set(activeEmbarques.map((e) => e.id)),
    [activeEmbarques]
  );

  // Docs belonging to active embarques
  const activeDocs = useMemo(
    () => allDocs.filter((d) => d.embarque_id && activeEmbarqueIds.has(d.embarque_id)),
    [allDocs, activeEmbarqueIds]
  );

  const activeDocIds = useMemo(() => activeDocs.map((d) => d.id), [activeDocs]);
  const { data: inventory = [] } = useInventory(activeDocIds.length > 0 ? activeDocIds : undefined);

  // Filter inventory to only active docs
  const activeInventory = useMemo(() => {
    const idSet = new Set(activeDocIds);
    return inventory.filter((i) => idSet.has(i.documento_id));
  }, [inventory, activeDocIds]);

  // Compliance lookup for arancel rates
  const hsKey = useMemo(
    () => activeInventory.map((i) => i.hs_code ?? "").join("|"),
    [activeInventory]
  );

  useEffect(() => {
    activeInventory.forEach((item, idx) => {
      const code = (item.hs_code ?? "").trim();
      if (code) complianceLookup(idx, code);
    });
  }, [hsKey, complianceLookup]);

  // KPI 2: Exposición Cambiaria (USD) — sum FOB of active docs
  const totalFobUSD = useMemo(
    () => activeDocs.reduce((sum, d) => sum + (parseFloat(String(d.total_factura)) || 0), 0),
    [activeDocs]
  );

  // KPI 1: Inventario en Tránsito (COP) — Landed Cost v2 (incluye IVA + Largest Remainder)
  // Agrupado por embarque para respetar trm_aduana, gastos_embarque y prorrateo correcto.
  const totalLandedCOP = useMemo(() => {
    if (!activeInventory.length || !trm) return 0;

    // Build arancel/iva maps keyed by item.id
    const arancelByItemId: Record<string, number> = {};
    const ivaByItemId: Record<string, number> = {};
    activeInventory.forEach((item, idx) => {
      const cr = complianceResults[idx];
      if (cr) {
        arancelByItemId[item.id] = item.aplica_tlc ? 0 : cr.gravamen_porcentaje;
        ivaByItemId[item.id] = cr.iva_porcentaje;
      }
    });

    // Group docs + items by embarque (legacy doc-level charges feed the engine pools).
    let total = 0;
    for (const emb of activeEmbarques) {
      const embDocs = activeDocs.filter((d) => d.embarque_id === emb.id);
      if (!embDocs.length) continue;
      const embDocIds = new Set(embDocs.map((d) => d.id));
      const embItems = activeInventory.filter((i) => embDocIds.has(i.documento_id));
      if (!embItems.length) continue;

      const result = calcShipmentLandedCost(
        embDocs,
        embItems,
        trmMap,
        arancelByItemId,
        [], // gastos_embarque: not loaded at dashboard level → legacy charges only
        emb.trm_aduana,
        ivaByItemId,
      );
      total += result.totalLandedCOP;
    }
    return total;
  }, [activeInventory, activeDocs, activeEmbarques, complianceResults, trm, trmMap]);

  const kpis = [
    {
      label: "Inventario en Tránsito",
      value: trm ? copFmt.format(totalLandedCOP) : "Sin TRM",
      sub: "Landed Cost v2 (COP)",
      icon: Package,
      accent: "text-emerald-500",
    },
    {
      label: "Exposición Cambiaria",
      value: usdFmt.format(totalFobUSD),
      sub: "Valor FOB activo",
      icon: TrendingUp,
      accent: "text-amber-500",
    },
    {
      label: "Embarques Activos",
      value: String(activeEmbarques.length),
      sub: `de ${embarques.length} totales`,
      icon: Ship,
      accent: "text-primary",
    },
    {
      label: "TRM Oficial",
      value: trm ? copFmt.format(trm) : "—",
      sub: trmFecha
        ? `Vigencia: ${new Date(trmFecha + "T00:00:00").toLocaleDateString("es-CO")}`
        : "Sin datos",
      icon: DollarSign,
      accent: "text-sky-500",
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {kpis.map((kpi) => (
        <Card key={kpi.label} className="border-border bg-card/80 backdrop-blur-sm">
          <CardContent className="p-4 flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">{kpi.label}</span>
              <kpi.icon className={`h-4 w-4 ${kpi.accent} opacity-70`} />
            </div>
            <p className={`text-xl font-bold tracking-tight tabular-nums text-foreground`}>
              {kpi.value}
            </p>
            <span className="text-[11px] text-muted-foreground">{kpi.sub}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
